import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconXSolid } from '@instructure/ui-icons';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { omitProps } from '@instructure/ui-react-utils';
import { isActiveElement } from '@instructure/ui-dom-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'DyQTK',
  template: function template(theme) {
    return "\n\n.DyQTK_eAEM{-moz-user-select:none;-ms-user-select:none;-webkit-user-select:none;align-items:center;box-sizing:border-box;display:inline-flex;font-family:".concat(theme.fontFamily || 'inherit', ";text-align:center;user-select:none;vertical-align:middle}\n\n[dir=ltr] .DyQTK_eAEM,[dir=rtl] .DyQTK_eAEM{text-align:center}\n\n.DyQTK_doqw{font-size:").concat(theme.fontSizeSmall || 'inherit', ";padding:").concat(theme.paddingSmall || 'inherit', "}\n\n.DyQTK_doqw .DyQTK_eWKC{line-height:calc(").concat(theme.heightSmall || 'inherit', " - ").concat(theme.defaultBorderWidth || 'inherit', "*2)}\n\n.DyQTK_doqw .DyQTK_dnnz{font-size:0.75rem}\n\n.DyQTK_ycrn{font-size:calc(").concat(theme.fontSizeMedium || 'inherit', " - 0.0625rem);padding:").concat(theme.padding || 'inherit', "}\n\n.DyQTK_ycrn .DyQTK_eWKC{line-height:calc(").concat(theme.heightMedium || 'inherit', " - ").concat(theme.defaultBorderWidth || 'inherit', "*2)}\n\n.DyQTK_ycrn .DyQTK_dnnz{font-size:0.75rem}\n\n.DyQTK_cMDj{font-size:calc(").concat(theme.fontSizeLarge || 'inherit', " - 0.0625rem);padding:").concat(theme.padding || 'inherit', "}\n\n.DyQTK_cMDj .DyQTK_eWKC{line-height:calc(").concat(theme.heightLarge || 'inherit', " - ").concat(theme.defaultBorderWidth || 'inherit', "*2)}\n\n.DyQTK_cMDj .DyQTK_dnnz{font-size:0.875rem}\n\n.DyQTK_EwaR{outline:none;overflow:visible;position:relative;touch-action:manipulation;transition:background-color ").concat(theme.transitionTiming || 'inherit', "}\n\n.DyQTK_EwaR:before{border:").concat(theme.focusOutlineWidth || 'inherit', " ").concat(theme.focusOutlineStyle || 'inherit', " ").concat(theme.focusOutlineColor || 'inherit', ";border-radius:").concat(theme.borderRadius || 'inherit', ";bottom:-0.3125rem;box-sizing:border-box;content:\"\";left:-0.3125rem;opacity:0;pointer-events:none;position:absolute;right:-0.3125rem;top:-0.3125rem;transform:scale(0.9);transition:all ").concat(theme.transitionTiming || 'inherit', "}\n\n.DyQTK_EwaR:not(.DyQTK_erdA){cursor:pointer}\n\n.DyQTK_EwaR:focus:before{opacity:1;transform:scale(1)}\n\n.DyQTK_EwaR.DyQTK_ywdX{cursor:not-allowed;opacity:0.5;pointer-events:none}\n\n.DyQTK_dnnz{-webkit-margin-end:0;-webkit-margin-start:").concat(theme.iconMargin || 'inherit', ";cursor:pointer;margin-inline-end:0;margin-inline-start:").concat(theme.iconMargin || 'inherit', ";transform:translateY(0.0625rem);transition:all ").concat(theme.transitionTiming || 'inherit', "}\n\n[dir=ltr] .DyQTK_dnnz{margin-left:").concat(theme.iconMargin || 'inherit', ";margin-right:0}\n\n[dir=rtl] .DyQTK_dnnz{margin-left:0;margin-right:").concat(theme.iconMargin || 'inherit', "}\n\n.DyQTK_eWKC{max-width:").concat(theme.maxWidth || 'inherit', ";overflow:hidden;text-overflow:ellipsis;white-space:nowrap}\n\n.DyQTK_ddES{background-color:").concat(theme.defaultBackground || 'inherit', ";border:").concat(theme.defaultBorderWidth || 'inherit', " ").concat(theme.defaultBorderStyle || 'inherit', " ").concat(theme.defaultBorderColor || 'inherit', ";border-radius:").concat(theme.defaultBorderRadius || 'inherit', ";color:").concat(theme.defaultColor || 'inherit', "}\n\n.DyQTK_ddES.DyQTK_EwaR:before{border-radius:").concat(theme.defaultBorderRadius || 'inherit', "}\n\n.DyQTK_ddES.DyQTK_EwaR:hover{background-color:").concat(theme.defaultBackgroundHover || 'inherit', "}\n\n.DyQTK_ddES.DyQTK_erdA .DyQTK_dnnz{color:").concat(theme.defaultIconColor || 'inherit', "}\n\n.DyQTK_ddES.DyQTK_erdA:hover .DyQTK_dnnz{color:").concat(theme.defaultIconHoverColor || 'inherit', "}\n\n.DyQTK_eXrk{background-color:").concat(theme.inlineBackground || 'inherit', ";border:").concat(theme.inlineBorderWidth || 'inherit', " ").concat(theme.inlineBorderStyle || 'inherit', " ").concat(theme.inlineBorderColor || 'inherit', ";border-radius:").concat(theme.inlineBorderRadius || 'inherit', ";color:").concat(theme.inlineColor || 'inherit', ";cursor:text;margin:0 0.1875rem 0.1875rem}\n\n.DyQTK_eXrk.DyQTK_EwaR:before{border-radius:calc(").concat(theme.inlineBorderRadius || 'inherit', "*1.5)}\n\n.DyQTK_eXrk.DyQTK_EwaR:hover{background-color:").concat(theme.inlineBackgroundHover || 'inherit', "}\n\n.DyQTK_eXrk.DyQTK_erdA .DyQTK_dnnz{background-color:").concat(theme.inlineIconColor || 'inherit', ";border-radius:50%;color:").concat(theme.inlineBackground || 'inherit', ";font-size:0.75rem;inset-inline-end:0;inset-inline-start:auto;padding:0.25rem;position:absolute;top:0;transform:translate(40%,-40%)}\n\n[dir=ltr] .DyQTK_eXrk.DyQTK_erdA .DyQTK_dnnz{left:auto;right:0}\n\n[dir=rtl] .DyQTK_eXrk.DyQTK_erdA .DyQTK_dnnz{left:0;right:auto}\n\n.DyQTK_eXrk.DyQTK_erdA:hover .DyQTK_dnnz{background-color:").concat(theme.inlineIconHoverColor || 'inherit', "}\n\n[dir=rtl] .DyQTK_eXrk.DyQTK_erdA .DyQTK_dnnz{transform:translate(-40%,-40%)}");
  },
  'tagRoot': 'DyQTK_eAEM',
  'small': 'DyQTK_doqw',
  'text': 'DyQTK_eWKC',
  'icon': 'DyQTK_dnnz',
  'medium': 'DyQTK_ycrn',
  'large': 'DyQTK_cMDj',
  'button': 'DyQTK_EwaR',
  'dismissible': 'DyQTK_erdA',
  'disabled': 'DyQTK_ywdX',
  'default': 'DyQTK_ddES',
  'inline': 'DyQTK_eXrk'
};
import theme from './theme';
/**
---
category: components
---
**/

var Tag = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(Tag, _Component);

  function Tag() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Tag);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Tag)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _this.focus = function () {
      _this._container && _this._container.focus();
    };

    _this.handleClick = function (e) {
      var _this$props = _this.props,
          disabled = _this$props.disabled,
          readOnly = _this$props.readOnly,
          onClick = _this$props.onClick;

      if (disabled || readOnly) {
        e.preventDefault();
        e.stopPropagation();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };

    _this.handleRef = function (node) {
      _this._container = node;
    };

    return _this;
  }

  _createClass(Tag, [{
    key: "render",
    value: function render() {
      var _classes;

      var _this$props2 = this.props,
          className = _this$props2.className,
          dismissible = _this$props2.dismissible,
          disabled = _this$props2.disabled,
          readOnly = _this$props2.readOnly,
          size = _this$props2.size,
          text = _this$props2.text,
          title = _this$props2.title,
          onClick = _this$props2.onClick,
          margin = _this$props2.margin,
          variant = _this$props2.variant;
      var classes = (_classes = {}, _defineProperty(_classes, styles.tagRoot, true), _defineProperty(_classes, styles[variant], true), _defineProperty(_classes, styles[size], size), _defineProperty(_classes, styles.dismissible, dismissible), _defineProperty(_classes, styles.button, onClick), _defineProperty(_classes, styles.disabled, disabled), _classes);
      var passthroughProps = View.omitViewProps(omitProps(this.props, Tag.propTypes), Tag);
      return React.createElement(View, Object.assign({}, passthroughProps, {
        ref: this.handleRef,
        elementRef: this.props.elementRef,
        className: classNames(className, classes),
        as: onClick ? 'button' : 'span',
        margin: margin,
        type: onClick ? 'button' : null,
        onClick: onClick ? this.handleClick : null,
        disabled: disabled || readOnly,
        display: null,
        title: title || (typeof text === 'string' ? text : null)
      }), React.createElement("span", {
        className: styles.text
      }, text), onClick && dismissible ? React.createElement(IconXSolid, {
        className: styles.icon
      }) : null);
    }
  }, {
    key: "focused",
    get: function get() {
      return isActiveElement(this._container);
    }
  }]);

  Tag.displayName = "Tag";
  return Tag;
}(Component), _class2.propTypes = {
  className: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  title: PropTypes.string,

  /**
   * Whether or not to disable the tag
   */
  disabled: PropTypes.bool,

  /**
   * Works just like disabled but keeps the same styles as if it were active
   */
  readOnly: PropTypes.bool,
  dismissible: PropTypes.bool,

  /**
  * Valid values are `0`, `none`, `auto`, `xxxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing,

  /**
  * If you add an onClick prop, Tag renders as a clickable button
  */
  onClick: PropTypes.func,

  /**
  * Provides a reference to the underlying html root element
  */
  elementRef: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['default', 'inline'])
}, _class2.defaultProps = {
  size: 'medium',
  dismissible: false,
  variant: 'default',
  elementRef: void 0,
  className: void 0,
  title: void 0,
  disabled: false,
  readOnly: false,
  margin: void 0,
  onClick: void 0
}, _temp)) || _class) || _class);
export default Tag;
export { Tag };